'use client'

import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import MUIInput from '@mui/material/TextField'
import MUIIcon from '@mui/material/Icon'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'

import { TextLinkButton } from '@repo/ui/TextLinkButton'

export const MultiSelectList = ({
  label = 'Select',
  items,
  value,
  onChange,
  valueKey = 'id',
  labelKey = 'description',
  idLabelKey = '',
  showIdInResults = true,
  sx = {}
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredItems, setFilteredItems] = useState(items)

  useEffect(() => {
    if (!searchTerm) {
      setFilteredItems(items)
      return
    }

    const lowerSearchTerm = searchTerm.toLowerCase()

    const filtered = items.filter((item) => {
      const labelMatch = String(item[labelKey])?.toLowerCase().includes(lowerSearchTerm)
      const valueMatch = String(item[valueKey])?.toLowerCase().includes(lowerSearchTerm)
      const idMatch = idLabelKey ? String(item[idLabelKey])?.toLowerCase().includes(lowerSearchTerm) : false
      return labelMatch || valueMatch || idMatch
    })

    setFilteredItems(filtered)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, items])

  const isOptionSelected = (option) => {
    if (!value) return false
    const match = value.find((item) => item[valueKey] === option[valueKey])
    return Boolean(match)
  }

  const handleSelect = (option) => {
    if (!value) {
      if (onChange) onChange([option])
      return
    }

    const match = value.find((item) => item[valueKey] === option[valueKey])
    const newValue = match ? value.filter((item) => item[valueKey] !== option[valueKey]) : [...value, option]
    if (onChange) onChange(newValue)
  }

  const handleSelectAll = () => {
    onChange(items)
  }

  const handleDeselectAll = () => {
    onChange([])
  }

  const renderOption = (option) => {
    const isSelected = isOptionSelected(option)
    const label = option[labelKey]
    const value = option[valueKey]

    return (
      <MUIStack
        direction="row"
        alignItems="center"
        gap={2}
        key={option[valueKey]}
        onClick={() => handleSelect(option)}
        tabindex={0}
        onKeyUp={(e) => e.code === 'Space' && handleSelect(option)}
        sx={{
          padding: '8px 10px',
          cursor: 'pointer',
          borderRadius: '4px',
          width: '100%',
          mb: '2px',
          backgroundColor: isSelected ? 'rgba(80, 115, 195, 0.12)' : 'white',
          '&:hover,&:focus': {
            backgroundColor: isSelected ? 'rgba(80, 115, 195, 0.12)' : 'rgba(80, 115, 195, 0.06)'
          }
        }}
      >
        <MUIIcon
          sx={{
            color: isSelected ? 'secondary.main' : 'text.secondary',
            mr: 2,
            mt: 0,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {isSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
        </MUIIcon>

        <MUIStack direction="column" sx={{ width: '100%', maxWidth: 'calc(100% - 100px)', position: 'relative' }}>
          {value && showIdInResults ? (
            <MUITypography
              sx={{ mb: 0, userSelect: 'none', pointerEvents: 'none', color: 'text.secondary' }}
              variant="body2"
              noWrap
            >
              {idLabelKey ? option[idLabelKey].toUpperCase() : String(value)?.toUpperCase()}
            </MUITypography>
          ) : null}
          {label ? (
            <MUITypography sx={{ mb: 0, userSelect: 'none', pointerEvents: 'none' }} variant="body1" noWrap>
              {label}
            </MUITypography>
          ) : null}
        </MUIStack>
      </MUIStack>
    )
  }

  return (
    <MUIStack
      data-testid="MultiSelectList"
      direction="column"
      sx={{
        borderRadius: '4px',
        mb: 6,
        minWidth: '300px',
        backgroundColor: 'background.paper',
        padding: '12px',
        ...sx
      }}
    >
      <MUIStack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        sx={{ width: '100%', mb: 2 }}
      >
        <MUITypography sx={{ mb: 0, userSelect: 'none', pointerEvents: 'none', color: 'text.primary' }} variant="h6">
          {label}
        </MUITypography>

        <MUIStack direction="row" justifyContent="flex-end" gap={3} sx={{ width: '100%' }}>
          <TextLinkButton sx={{ width: 'auto' }} onClick={handleSelectAll} title="Select all" />
          <TextLinkButton sx={{ width: 'auto' }} onClick={handleDeselectAll} title="Select none" />
        </MUIStack>
      </MUIStack>

      <MUIInput
        sx={{ mb: 2, input: { backgroundColor: 'white' } }}
        placeholder="Search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      <MUIStack
        direction="column"
        sx={{
          overflowX: 'hidden',
          overflowY: 'auto',
          height: '365px',
          borderRadius: '4px',
          backgroundColor: 'white',
          padding: '6px'
        }}
      >
        {filteredItems?.map((option) => {
          return renderOption(option)
        })}
      </MUIStack>

      <MUITypography sx={{ mt: 2, mb: 0, color: 'text.secondary', width: '100%', textAlign: 'right' }} variant="body1">
        {value?.length || 0} / {items?.length || 0} items selected
      </MUITypography>
    </MUIStack>
  )
}

MultiSelectList.propTypes = {
  label: PropTypes.string,
  items: PropTypes.array,
  value: PropTypes.array,
  onChange: PropTypes.func,
  valueKey: PropTypes.string,
  labelKey: PropTypes.string,
  idLabelKey: PropTypes.string,
  showIdInResults: PropTypes.bool,
  name: PropTypes.string,
  errors: PropTypes.object,
  sx: PropTypes.object
}
